import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, X, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  playNotificationSound,
  checkForNewEvents,
  areNotificationsEnabled,
  setNotificationsEnabled,
  requestNotificationPermission,
  showBrowserNotification
} from '@/services/notificationService';

const NewEventNotification = ({ events }) => {
  const [newEvents, setNewEvents] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(areNotificationsEnabled());
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Only check once when events are loaded
    if (events.length > 0 && !hasChecked) {
      const newOnes = checkForNewEvents(events);
      
      if (newOnes.length > 0) {
        setNewEvents(newOnes);
        setShowNotification(true);
        
        // Play sound if enabled
        if (soundEnabled) {
          playNotificationSound();
        }
        
        // Show browser notification for each new event
        newOnes.forEach(event => {
          showBrowserNotification(event);
        });
      }
      
      setHasChecked(true);
    }
  }, [events, hasChecked, soundEnabled]);

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  const toggleSound = () => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    setNotificationsEnabled(newState);
    
    // Play a test sound when enabling
    if (newState) {
      playNotificationSound();
    }
  };

  const dismissNotification = () => {
    setShowNotification(false);
  };

  if (!showNotification || newEvents.length === 0) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-4 right-4 z-50 max-w-sm animate-in slide-in-from-right duration-300"
      data-testid="new-event-notification"
    >
      <div className="bg-obsidian-light border border-electric/30 rounded-lg shadow-xl shadow-electric/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-electric/10 border-b border-electric/20">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-electric animate-pulse" />
            <span className="text-electric font-medium text-sm">
              {newEvents.length === 1 ? 'New Event!' : `${newEvents.length} New Events!`}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleSound}
              className="p-1.5 text-white/50 hover:text-white rounded transition-colors"
              title={soundEnabled ? 'Mute notifications' : 'Enable sound'}
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4" />
              ) : (
                <VolumeX className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={dismissNotification}
              className="p-1.5 text-white/50 hover:text-white rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Events List */}
        <div className="max-h-64 overflow-y-auto">
          {newEvents.slice(0, 3).map(event => (
            <Link
              key={event.id}
              to={`/events/${event.id}`}
              onClick={dismissNotification}
              className="block px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
            >
              <p className="text-white font-medium text-sm truncate">{event.title}</p>
              <p className="text-white/50 text-xs mt-0.5">
                {event.date} at {event.time}
              </p>
            </Link>
          ))}
        </div>

        {/* Footer */}
        {newEvents.length > 3 && (
          <div className="px-4 py-2 bg-white/5 border-t border-white/10">
            <Link
              to="/events"
              onClick={dismissNotification}
              className="text-electric text-xs hover:underline"
            >
              +{newEvents.length - 3} more events →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewEventNotification;
